import { updateWorkflowTemplateVersion } from '@commenda/api';
import {
  createAgentConversation,
  sendAgentConversationMessage,
  WORKFLOW_BUILDER_PROFILE_KEY,
} from '@commenda/api/commenda-ai';
import {
  AgentAttachment,
  AgentMode,
  BuilderChatContext,
} from '@commenda/types';
import { useCallback, useState } from 'react';
import {
  TBuilderCanvasBinding,
  TWorkflowVersionTarget,
  WorkflowVersionTargetOutcome,
} from './types/types';

export function useBuilderChatSend(
  params: Readonly<{
    workflowTemplateVersionId: string;
    conversationId: string | null;
    canvas: TBuilderCanvasBinding;
    isEditable: boolean;
    resolveTarget: (mode: AgentMode) => Promise<TWorkflowVersionTarget>;
    onConversationCreated: (conversationId: string) => void;
    onConversationsChanged: () => void;
    onRunStarted: () => void;
    /** Read at send time, so the agent is told what was on screen when the author pressed enter and not
     *  what was on screen when the panel rendered. Null sends nothing. */
    getBuilderContext?: () => BuilderChatContext | null;
  }>
) {
  const {
    workflowTemplateVersionId,
    conversationId,
    canvas,
    isEditable,
    resolveTarget,
    onConversationCreated,
    onConversationsChanged,
    onRunStarted,
    getBuilderContext,
  } = params;

  const [error, setError] = useState<string | null>(null);

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (conversationId) return conversationId;

    const created = await createAgentConversation({
      profile: [WORKFLOW_BUILDER_PROFILE_KEY],
    });
    const createdId = created.data?.conversation.conversationId;
    if (!createdId) return null;

    onConversationCreated(createdId);
    onConversationsChanged();
    return createdId;
  }, [conversationId, onConversationCreated, onConversationsChanged]);

  /** Resolves false once `error` is set. The composer clears on any resolve, so the panel turns
   *  false into a rejection to keep the author's text. */
  const send = useCallback(
    async (
      content: string,
      attachments: AgentAttachment[],
      options: { mode: AgentMode; modelId: string }
    ): Promise<boolean> => {
      setError(null);

      // Never save state that arrived from a poll: applyRemoteWorkflow resets the baseline, so unsaved
      // changes are only ever the author's own edits and a blind save would be a revert.
      if (canvas.hasUnsavedChanges() && canvas.workflow && isEditable) {
        const saveResult = await updateWorkflowTemplateVersion(
          workflowTemplateVersionId,
          { definition: canvas.workflow }
        );
        if (!saveResult.ok) {
          setError(
            'Could not save your canvas edits, so nothing was sent. Try saving from the toolbar first.'
          );
          return false;
        }
        canvas.markAsSaved();
      }

      const target = await resolveTarget(options.mode);
      if (target.outcome === WorkflowVersionTargetOutcome.FAILED) {
        setError(target.error);
        return false;
      }

      const activeConversationId = await ensureConversation();
      if (!activeConversationId) {
        setError('Could not start a chat for this workflow.');
        return false;
      }

      const builderContext = getBuilderContext?.() ?? undefined;

      const sent = await sendAgentConversationMessage(activeConversationId, {
        message: content,
        attachments,
        mode: options.mode,
        modelId: options.modelId,
        targetWorkflowTemplateVersionId: target.targetWorkflowTemplateVersionId,
        ...(builderContext ? { builderContext } : {}),
      });
      if (!sent.success) {
        setError('The message could not be sent.');
        return false;
      }

      onRunStarted();
      return true;
    },
    [
      canvas,
      isEditable,
      workflowTemplateVersionId,
      resolveTarget,
      ensureConversation,
      onRunStarted,
      getBuilderContext,
    ]
  );

  return { send, error };
}
